<script setup lang="ts">
    import { computed } from 'vue'

    import type { ScoresExporterAlternativeReadModel } from '@Evaluation/AlternativesSurveys/Domain/readmodels'
    import { ScoresExporterViewModel } from '@Evaluation/AlternativesSurveys/Components/ScoresExporter'

    /* ---------------- props / emits ---------------- */

    const props = defineProps<{
        viewModel: ScoresExporterViewModel
    }>()

    const emit = defineEmits<{
        (e: 'close-dialog'): void
        (e: 'export-scores', mappings: { RefCriterionId: number; AttributeId: number }[]): void
        (e: 'reset-preview'): void
        (e: 'load-preview', mappings: { RefCriterionId: number; AttributeId: number }[]): void
        (e: 'show-attribute-creator'): void
    }>()

    /* ---------------- shortcuts ---------------- */

    const vm = computed(() => props.viewModel)
    const opened = computed(() => vm.value?.opened ?? false)

    /* ---------------- derived ---------------- */

    const MappedAttributeIds = computed<number[]>(() =>
        vm.value.criteria
            .filter(c => c.AttributeId != null)
            .map(c => c.AttributeId)
    )

    /* ---------------- helpers ---------------- */

    function IsExact(alternative: ScoresExporterAlternativeReadModel, attrId: number): boolean
    {
        return alternative.Scores.find(s => s.AttributeId === attrId).IsExact
    }

    function HasValue(alternative: ScoresExporterAlternativeReadModel, attrId: number): boolean
    {
        return alternative.Scores.find(s => s.AttributeId === attrId).AttributeValue != null
    }

    function HasDefinedAttributeValues(alternative: ScoresExporterAlternativeReadModel, attrId: number): boolean
    {
        return alternative.Scores.find(s => s.AttributeId === attrId).HasDefinedAttributeValues
    }

    function getScore(alternative: ScoresExporterAlternativeReadModel, attrId: number): number
    {
        return alternative.Scores.find(s => s.AttributeId === attrId).Score
    }

    function getAttributeValue(alternative: ScoresExporterAlternativeReadModel, attrId: number): number
    {
        return alternative.Scores.find(s => s.AttributeId === attrId).AttributeValue
    }

    function getAttributeValueLabel(alternative: ScoresExporterAlternativeReadModel, attrId: number): string
    {
        return alternative.Scores.find(s => s.AttributeId === attrId).AttributeValueLabel
    }

    function getAttributeName(attrId: number): string
    {
        return vm.value.attributes.find(a => a.Id === attrId).Name
    }

    /* ---------------- actions ---------------- */

    function close()
    {
        emit('close-dialog')
    }

    function exportScores()
    {
        const mappings = vm.value.criteria
            .filter(c => c.AttributeId != null)
            .map(c => ({
                RefCriterionId: c.Id,
                AttributeId: c.AttributeId
            }))

        emit('export-scores', mappings)
    }

    function resetPreview()
    {
        emit('reset-preview')
    }

    function loadPreview()
    {
        const mappings = vm.value.criteria
            .filter(c => c.AttributeId != null)
            .map(c => ({
                RefCriterionId: c.Id,
                AttributeId: c.AttributeId
            }))

        emit('load-preview', mappings)
    }

    function createAttribute()
    {
        emit('show-attribute-creator')
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Save scores to attributes</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <template v-if="vm.initialized === false">
                            <h3><i class="fa fa-spinner fa-spin"></i> Loading...</h3>
                        </template>

                        <template v-else-if="vm.initialized === true && vm.preview == null">
                            <table class="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Criterion</th>
                                        <th>Attribute</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="c in vm.criteria" :key="c.Id">
                                        <td>
                                            {{c.Name}}
                                            <br />
                                            <span class="text-muted">{{c.Path}}</span>
                                        </td>
                                        <td>
                                            <select class="form-control" v-model="c.AttributeId">
                                                <option :value="null">None</option>
                                                <option v-for="attr in vm.attributes" :key="attr.Id" :value="attr.Id">{{attr.Name}}</option>
                                            </select>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </template>

                        <template v-else-if="vm.initialized === true && vm.preview != null">
                            <table class="table table-hover table-bordered">
                                <thead>
                                    <tr>
                                        <th>Alternative</th>
                                        <th v-for="attrId in MappedAttributeIds" :key="attrId">{{getAttributeName(attrId)}}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="alternative in vm.alternatives" :key="alternative.Name">
                                        <td>
                                            {{alternative.Name}}
                                        </td>

                                        <td v-for="attrId in MappedAttributeIds" :key="attrId" :class="{'bg-warning': IsExact(alternative, attrId) === false}">
                                            <div v-if="HasValue(alternative, attrId)">
                                                <span v-if="HasDefinedAttributeValues(alternative, attrId) === false">{{getScore(alternative, attrId)}}</span>
                                                <span v-if="HasDefinedAttributeValues(alternative, attrId) === true">
                                                    {{getAttributeValueLabel(alternative, attrId)}} ({{getAttributeValue(alternative, attrId)}})
                                                </span>

                                                <span v-if="IsExact(alternative, attrId) === false" class="text-muted">
                                                    <br />Source score: {{getScore(alternative, attrId)}}
                                                </span>
                                            </div>

                                        </td>

                                    </tr>
                                </tbody>
                            </table>

                        </template>

                    </div>

                    <div class="modal-footer">
                        <template v-if="vm.preview == null">
                            <button type="button" @click="createAttribute" class="btn btn-outline-secondary">New attribute</button>
                            <button type="button" @click="loadPreview" :disabled="MappedAttributeIds.length == 0" class="btn btn-primary">Preview</button>
                        </template>
                        <template v-else>
                            <button type="button" @click="resetPreview" class="btn btn-outline-secondary">Back</button>
                            <button type="button" @click="exportScores" class="btn btn-success">Save</button>
                        </template>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>  